import React, { useRef } from "react";
import PropTypes from "prop-types";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { useLocation } from "react-router";

import "./StaggeredAnimation.css";

export function StaggeredAnimation({ animationKey, children, onAnimationEnd }) {
  const { pathname } = useLocation();

  const childList = React.Children.toArray(children);
  const nodeRef = useRef(null); // https://github.com/reactjs/react-transition-group/issues/668
  // Static render targets (the patient /pdf/ view and the Designer /print
  // bundle) are captured as one-shot PDFs in print media, where the
  // settle-to-0 animation never runs. Zero the stagger offset for both so
  // items don't keep their initial translateY and print with phantom gaps.
  const isStaticRender =
    pathname.startsWith("/pdf/") || pathname.startsWith("/print");

  return (
    <SwitchTransition>
      <CSSTransition
        key={animationKey}
        appear={true}
        timeout={400}
        classNames="staggered-animation"
        nodeRef={nodeRef}
      >
        <div className="staggered-animation" ref={nodeRef}>
          {childList
            .filter(Boolean)
            .map((child, i, visibleChildren) => {
              const className = child.props.className
                ? `${child.props.className} staggered-animation__item`
                : "staggered-animation__item";

              const isLastChild = i === visibleChildren.length - 1;
              const animationOffsetY = isStaticRender ? 0 : 2;
              return React.cloneElement(child, {
                className,
                style: {
                  transform: `translateY(${(i + 1) * animationOffsetY}rem)`,
                },
                ...(isLastChild && onAnimationEnd && { onAnimationEnd }),
              });
            })}
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
}

StaggeredAnimation.propTypes = {
  children: PropTypes.node,
  animationKey: PropTypes.string.isRequired,
  onAnimationEnd: PropTypes.func,
};
